import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero } from "@/components/page-hero";
import { useState, useMemo } from "react";
import { Download, FileText } from "lucide-react";
import { templates, templateCategories } from "@/content/templates";
import { InternalLinksHub } from "@/components/internal-links-hub";
import { EabBannerBlock } from "@/components/eab-banner";

export const Route = createFileRoute("/hr-templates/")({
  head: () => ({
    meta: [
      { title: "Free UK HR Templates, Contracts, Letters & Policies" },
      {
        name: "description",
        content:
          "Free, solicitor-drafted UK HR templates: employment contracts, disciplinary letters, redundancy scripts, policies and onboarding checklists.",
      },
      { property: "og:title", content: "Free UK HR Templates" },
      { property: "og:description", content: "Solicitor-drafted UK HR templates, free to download." },
      { property: "og:url", content: "https://www.hrservicecentre.co.uk/hr-templates" },
    ],
    links: [{ rel: "canonical", href: "https://www.hrservicecentre.co.uk/hr-templates" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Free UK HR Templates, Contracts, Letters & Policies",
          url: "https://www.hrservicecentre.co.uk/hr-templates",
          description:
            "Free, solicitor-drafted UK HR templates: contracts, disciplinary letters, redundancy scripts, policies and onboarding checklists.",
          isPartOf: { "@id": "https://www.hrservicecentre.co.uk/#website" },
          mainEntity: {
            "@type": "ItemList",
            itemListElement: templates.map((t, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `https://www.hrservicecentre.co.uk/hr-templates/${t.slug}`,
              name: t.title,
            })),
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://www.hrservicecentre.co.uk/" },
            { "@type": "ListItem", position: 2, name: "Resources", item: "https://www.hrservicecentre.co.uk/hr-resources" },
            { "@type": "ListItem", position: 3, name: "Templates", item: "https://www.hrservicecentre.co.uk/hr-templates" },
          ],
        }),
      },
    ],
  }),
  component: TemplatesIndex,
});

function TemplatesIndex() {
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState<string>("All");

  const filtered = useMemo(() => {
    return templates.filter((t) => {
      const matchesQ = !query || t.title.toLowerCase().includes(query.toLowerCase());
      const matchesC = cat === "All" || t.category === cat;
      return matchesQ && matchesC;
    });
  }, [query, cat]);

  return (
    <>
      <PageHero
        breadcrumbSchema={false}
        breadcrumbs={[
          { label: "Home", path: "/" },
          { label: "Resources", path: "/hr-resources" },
          { label: "Templates", path: "/hr-templates" },
        ]}
        eyebrow="Templates"
        title="Free UK HR Templates for Employers"
        subtitle="Free HR templates, contracts and policies, drafted by UK employment solicitors."
        intro="Solicitor-drafted contracts, letters, policies and checklists. All free to download, all reviewed and updated for 2026."
      />

      <section className="px-6 py-16 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search templates…"
              className="w-full rounded-md border border-border bg-card px-4 py-2.5 text-sm focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent sm:max-w-sm"
            />
            <select
              value={cat}
              onChange={(e) => setCat(e.target.value)}
              className="rounded-md border border-border bg-card px-4 py-2.5 text-sm focus:border-accent focus:outline-none"
            >
              <option value="All">All categories</option>
              {templateCategories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <span className="text-sm text-muted-foreground">
              Showing {filtered.length} of {templates.length}
            </span>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((t) => (
              <Link
                key={t.slug}
                to="/hr-templates/$slug"
                params={{ slug: t.slug }}
                className="group flex flex-col rounded-2xl bg-card p-6 ring-1 ring-black/5 transition hover:-translate-y-0.5 hover:ring-accent/30"
              >
                <div className="flex items-start justify-between">
                  <span className="inline-flex size-10 items-center justify-center rounded-lg bg-accent/10 text-accent">
                    <FileText className="size-5" />
                  </span>
                  <span className="text-xs font-medium text-muted-foreground">{t.format}</span>
                </div>
                <h2 className="mt-5 font-serif text-lg leading-snug text-brand">{t.title}</h2>
                <p className="mt-2 flex-1 text-sm text-muted-foreground">{t.description}</p>
                <div className="mt-4 text-xs font-medium uppercase tracking-wider text-accent">{t.category}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <EabBannerBlock membership source="templates_hub" />

      <InternalLinksHub
        heading="Everything UK employers need from an HR partner"
        intro="Browse our most-requested services, sector pages and free resources, all written by senior HR and employment law specialists."
      />
    </>
  );
}
